import React, { useState, useEffect } from 'react';
import { useAPI } from 'api';
import { LicenseStatusType, LicenseStatusInfo } from 'types';

const LicenseStatus = () => {
	const [licenseStatus, setLicenseStatus] = useState<LicenseStatusInfo | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const api = useAPI();

	useEffect(() => {
		fetchLicenseStatus();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchLicenseStatus = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await api.get<LicenseStatusInfo>('/api/system/licensing/status');
			setLicenseStatus(response);
		} catch (err) {
			setError((err as Error).message || 'Failed to fetch license status');
			console.error('Error fetching license status:', err);
		} finally {
			setLoading(false);
		}
	};

	if (loading) {
		return <div>Loading license status...</div>;
	}

	if (error) {
		return (
			<div>
				Error: {error}
				<button onClick={fetchLicenseStatus} className="button.link">
					Retry
				</button>
			</div>
		);
	}

	if (!licenseStatus) {
		return <div>No license status available</div>;
	}

	const getStatusColor = (status: LicenseStatusType) => {
		switch (status) {
			case 'valid':
				return 'status-valid';
			case 'trial':
				return 'status-trial';
			case 'stale':
				return 'status-stale';
			case 'expired':
				return 'status-expired';
			case 'error':
				return 'status-error';
			case 'unlicensed':
				return 'status-unlicensed';
			case 'not-applicable':
				return 'status-na';
			case 'invalid':
				return 'status-invalid';
			default:
				return 'status-unknown';
		}
	};

	const getStatusIcon = (status: LicenseStatusType) => {
		switch (status) {
			case 'valid':
				return '✓';
			case 'trial':
				return '🔄';
			case 'stale':
				return '⚠';
			case 'expired':
				return '✗';
			case 'error':
				return '❌';
			case 'unlicensed':
				return '⊘';
			case 'not-applicable':
				return '—';
			case 'invalid':
				return '✗';
			default:
				return '?';
		}
	};

	const getStatusWithDetails = (): string => {
		if (!licenseStatus) return '';
		let statusText: string = licenseStatus.status;
		const details: string[] = [];

		// Determine which license type is active and add relevant details
		if (licenseStatus.status === 'trial' || (licenseStatus.trial.status === 'valid' && licenseStatus.license.status === 'not-applicable')) {
			details.push('Trial');
			if (licenseStatus.trial.daysRemain !== undefined && licenseStatus.trial.daysRemain > 0) {
				details.push(`${licenseStatus.trial.daysRemain} days remaining`);
			}
			if (licenseStatus.trial.expiresAt) {
				details.push(`expires ${new Date(licenseStatus.trial.expiresAt).toLocaleDateString()}`);
			}
		} else if (licenseStatus.license.status !== 'not-applicable') {
			details.push('License');
			if (licenseStatus.license.daysRemain !== undefined && licenseStatus.license.daysRemain > 0) {
				details.push(`${licenseStatus.license.daysRemain} days remaining`);
			}
			if (licenseStatus.license.expiresAt) {
				details.push(`expires ${new Date(licenseStatus.license.expiresAt).toLocaleDateString()}`);
			}
		}

		if (details.length > 0) {
			statusText += ` (${details.join(', ')})`;
		}

		return statusText;
	};

	return (
		<div>
			<div className="status-overview">
				<div className={`status-item ${getStatusColor(licenseStatus.status)}`}>
					<span className="status-icon">{getStatusIcon(licenseStatus.status)}</span>
					<span className="status-label">Overall Status:</span>
					<span className="status-value">{getStatusWithDetails()}</span>
				</div>
			</div>

			<div className="license-details">
				<div className="detail-section">
					<h4>Trial</h4>
					<div className={`status-item ${getStatusColor(licenseStatus.trial.status)}`}>
						<span className="status-icon">{getStatusIcon(licenseStatus.trial.status)}</span>
						<span className="status-label">Status:</span>
						<span className="status-value">{licenseStatus.trial.status}</span>
					</div>
					{licenseStatus.trial.daysRemain !== undefined && licenseStatus.trial.daysRemain > 0 && (
						<div className="detail-item">
							<span className="detail-label">Days Remaining:</span>
							<span className="detail-value">{licenseStatus.trial.daysRemain}</span>
						</div>
					)}
					{licenseStatus.trial.expiresAt && (
						<div className="detail-item">
							<span className="detail-label">Expires:</span>
							<span className="detail-value">
								{new Date(licenseStatus.trial.expiresAt).toLocaleDateString()}
							</span>
						</div>
					)}
				</div>

				<div className="detail-section">
					<h4>License</h4>
					<div className={`status-item ${getStatusColor(licenseStatus.license.status)}`}>
						<span className="status-icon">{getStatusIcon(licenseStatus.license.status)}</span>
						<span className="status-label">Status:</span>
						<span className="status-value">{licenseStatus.license.status}</span>
					</div>
					{licenseStatus.license.daysRemain !== undefined && licenseStatus.license.daysRemain > 0 && (
						<div className="detail-item">
							<span className="detail-label">Days Remaining:</span>
							<span className="detail-value">{licenseStatus.license.daysRemain}</span>
						</div>
					)}
					{licenseStatus.license.expiresAt && (
						<div className="detail-item">
							<span className="detail-label">Expires:</span>
							<span className="detail-value">
								{new Date(licenseStatus.license.expiresAt).toLocaleDateString()}
							</span>
						</div>
					)}
				</div>

				<div className="detail-section">
					<h4>System</h4>
					<div className="detail-item">
						<span className="detail-label">Pending Requests:</span>
						<span className="detail-value">{licenseStatus.pendingRequests}</span>
					</div>
					{licenseStatus.commsError && (
						<div className="detail-item">
							<span className="detail-label">Comms Error:</span>
							<span className="detail-value status-error">{licenseStatus.commsError}</span>
						</div>
					)}
				</div>
			</div>

			<button onClick={fetchLicenseStatus} className="button.link">
				Refresh Status
			</button>
		</div>
	);
};

export default LicenseStatus;
